import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import httpService from "../../../../services/httpServices";
import AffiliateDashboard from "./AffiliateDashboard";
import AffiliateLinks from "./AffiliateLinks";
import AffiliatePayoutCenter from "./AffiliatePayoutCenter";
import AffiliateSettings from "./AffiliateSettings";

/**
 * Affiliate program root.
 *
 * Per Kim's spec there is no enrollment gate — every user is auto-enrolled
 * server-side. We just fetch the profile and render the type-aware
 * dashboard. If the profile fetch fails the backend will create the record
 * lazily on the next request, so we just retry once.
 */
const AffiliatePage = () => {
  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAffiliateProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await httpService.get("/affiliate/profile");
      if (data.success) {
        setAffiliate(data.data);
      } else {
        setError("Could not load affiliate profile");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Could not load affiliate profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliateProfile();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-[#6a1fbf] border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400">Loading affiliate program...</p>
        </div>
      </div>
    );
  }

  if (error || !affiliate) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 max-w-md text-center space-y-4">
          <p className="text-red-400">{error || "Affiliate profile unavailable"}</p>
          <button
            onClick={fetchAffiliateProfile}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 text-white px-6 py-2.5 rounded-lg font-bold transition-opacity"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<AffiliateDashboard affiliate={affiliate} />} />
      <Route path="/links" element={<AffiliateLinks affiliate={affiliate} />} />
      {/* New canonical path */}
      <Route path="/payouts" element={<AffiliatePayoutCenter affiliate={affiliate} />} />
      {/* Backwards compatibility for the old payout-center URL */}
      <Route path="/payout-center" element={<AffiliatePayoutCenter affiliate={affiliate} />} />
      <Route
        path="/settings"
        element={<AffiliateSettings affiliate={affiliate} onUpdate={fetchAffiliateProfile} />}
      />
      <Route path="*" element={<Navigate to="/dashboard/affiliate" replace />} />
    </Routes>
  );
};

export default AffiliatePage;
